// The organizer register ("Klubber og arrangører"): every decision that needs
// no DOM and no Firestore, so it can be tested under node:test.
//
// Data access is in js/services/organizers.js, rendering in
// js/ui/organizers-page.js. The shape of the two documents is described, and
// enforced, in firestore.rules (organizers / organizerRosters).
//
// NAMING. "Organizer" here is the club, school or federation behind a
// tournament. The account role claim `organizer` is something else entirely —
// it is the old internal name for an arbiter account — and nothing in this
// file touches it.

export const ORGANIZER_TYPES = Object.freeze(['club', 'school', 'federation', 'company', 'other']);

// English source strings, translated through tr() where they are shown.
export const ORGANIZER_TYPE_LABELS = Object.freeze({
    club: 'Club',
    school: 'School',
    federation: 'Federation / region',
    company: 'Company',
    other: 'Other'
});

export const ROLE_ADMIN = 'admin';
export const ROLE_ARBITER = 'arbiter';

const MAX_NAME = 120;
const MAX_SHORT_NAME = 8;
const MAX_TEXT = 120;
const MAX_PHONE = 30;
const MAX_WEBSITE = 200;

export function normEmail(email) {
    return String(email || '').trim().toLowerCase();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email) {
    return EMAIL_RE.test(normEmail(email));
}

/**
 * The name reduced to lowercase letters and digits — what two spellings of the
 * same club have in common ("Nordlys Skolesjakk", "nordlys skole-sjakk").
 * Letters outside a-z are kept: æ, ø and å are part of Norwegian club names,
 * and folding them away would make "Ås" and "As" the same club.
 */
export function organizerNameKey(name) {
    return String(name || '').toLowerCase().normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '');
}

/** Up to three letters for the logo placeholder: the short name, or initials. */
export function organizerInitials(org) {
    const o = org || {};
    const short = String(o.shortName || '').trim();
    if (short) return short.slice(0, 3).toUpperCase();
    const words = String(o.name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

/** "nordlys.no" -> "https://nordlys.no"; '' stays ''. Anything but http(s) is dropped. */
export function normalizeWebsite(url) {
    const s = String(url || '').trim();
    if (!s) return '';
    const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : 'https://' + s;
    return /^https?:\/\//i.test(withScheme) ? withScheme : '';
}

/** A website as a reader would say it: no scheme, no trailing slash. */
export function displayWebsite(url) {
    return String(url || '').replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

/**
 * The register / edit form's values -> the profile document's fields, or the
 * reasons it cannot be saved. Errors are English source strings for tr().
 */
export function validateOrganizerForm(values = {}) {
    const text = (k, max) => String(values[k] == null ? '' : values[k]).trim().slice(0, max);
    const errors = [];

    const name = text('name', MAX_NAME + 1);
    if (!name) errors.push('Give the organizer a name.');
    else if (name.length > MAX_NAME) errors.push('The name is too long.');

    const email = normEmail(values.email);
    if (email && !isValidEmail(email)) errors.push('The contact email address does not look right.');

    const rawWebsite = String(values.website || '').trim();
    const website = normalizeWebsite(rawWebsite);
    if (rawWebsite && !website) errors.push('The website must be an ordinary web address.');

    const federation = text('federation', 3).toUpperCase();

    const profile = {
        name: name.slice(0, MAX_NAME),
        shortName: text('shortName', MAX_SHORT_NAME),
        type: ORGANIZER_TYPES.includes(values.type) ? values.type : 'club',
        city: text('city', MAX_TEXT),
        federation: /^[A-Z]{3}$/.test(federation) ? federation : '',
        email,
        phone: text('phone', MAX_PHONE),
        website: website.slice(0, MAX_WEBSITE),
        logoUrl: String(values.logoUrl || '').trim()
    };
    profile.nameKey = organizerNameKey(profile.name);

    return { ok: errors.length === 0, errors, profile };
}

// ---------------------------------------------------------------------------
// The roster
// ---------------------------------------------------------------------------

function list(roster, key) {
    return Array.isArray(roster && roster[key]) ? roster[key].map(normEmail) : [];
}

/** 'admin' | 'arbiter' | 'pendingAdmin' | 'pendingArbiter' | null */
export function roleIn(roster, email) {
    const e = normEmail(email);
    if (!e) return null;
    if (list(roster, 'admins').includes(e)) return 'admin';
    if (list(roster, 'arbiters').includes(e)) return 'arbiter';
    if (list(roster, 'pendingAdmins').includes(e)) return 'pendingAdmin';
    if (list(roster, 'pendingArbiters').includes(e)) return 'pendingArbiter';
    return null;
}

export function isMemberRole(role) {
    return role === 'admin' || role === 'arbiter';
}

export function isPendingRole(role) {
    return role === 'pendingAdmin' || role === 'pendingArbiter';
}

export function memberCount(roster) {
    return list(roster, 'admins').length + list(roster, 'arbiters').length;
}

/**
 * The roster as the Arbiters table shows it: admins, then arbiters, then open
 * invitations; each group sorted by name (or address, for someone who has not
 * put a name on the roster).
 */
export function rosterRows(roster, me = '') {
    const names = (roster && roster.names) || {};
    const myEmail = normEmail(me);
    const row = (email, role, pending) => ({
        email,
        name: typeof names[email] === 'string' ? names[email].trim() : '',
        role,
        pending,
        isMe: email === myEmail
    });
    const byName = (a, b) => (a.name || a.email).localeCompare(b.name || b.email, 'nb', { sensitivity: 'base' });
    const group = (key, role, pending) => list(roster, key).map(e => row(e, role, pending)).sort(byName);
    return [
        ...group('admins', ROLE_ADMIN, false),
        ...group('arbiters', ROLE_ARBITER, false),
        ...group('pendingAdmins', ROLE_ADMIN, true),
        ...group('pendingArbiters', ROLE_ARBITER, true)
    ];
}

/**
 * Whether `email` may leave (or be removed, or be demoted) without leaving the
 * club with no admin. The rules refuse it anyway; this is for a clear message.
 */
export function wouldLeaveNoAdmin(roster, email) {
    const admins = list(roster, 'admins');
    return admins.length === 1 && admins[0] === normEmail(email);
}

/**
 * Why an invitation cannot be sent, as an English source string, or '' when it
 * can. The account lookup (is this an arbiter account at all?) happens after
 * this, on the server.
 */
export function inviteProblem(roster, email, me) {
    const target = normEmail(email);
    if (!isValidEmail(target)) return 'Please enter a valid email address.';
    if (target === normEmail(me)) return 'You are already a member of this organizer.';
    const role = roleIn(roster, target);
    if (isMemberRole(role)) return 'This arbiter is already a member of this organizer.';
    if (isPendingRole(role)) return 'This arbiter has already been invited and has not answered yet.';
    return '';
}

// ---------------------------------------------------------------------------
// Deleting an account
// ---------------------------------------------------------------------------

/**
 * Whether anyone besides `email` has ACCEPTED a place on the roster. Open
 * invitations do not count: nobody has agreed to run the club yet.
 */
export function hasOtherMembers(roster, email) {
    const me = normEmail(email);
    return [...list(roster, 'admins'), ...list(roster, 'arbiters')].some(e => e !== me);
}

/**
 * What "Delete my profile" does to each club the account is on, decided before
 * anything is deleted. `items` are listMyOrganizers()-shaped: { id, name,
 * roster, myRole }.
 *
 *   blockers     the account is the club's only admin and other arbiters still
 *                belong to it. Deleting the account would leave a club nobody
 *                can manage, so it waits until someone else is made admin, or
 *                the club is deleted.
 *   deletes      the account is the club's only member. The club goes with it;
 *                invitations it has sent are withdrawn.
 *   leaves       the account comes off the member list; others run the club.
 *   invitations  open invitations to the account, quietly declined.
 */
export function accountExitPlan(items, email) {
    const plan = { blockers: [], deletes: [], leaves: [], invitations: [] };
    (items || []).forEach(item => {
        const role = roleIn(item.roster, email);
        if (isPendingRole(role)) {
            plan.invitations.push(item);
        } else if (isMemberRole(role)) {
            if (!wouldLeaveNoAdmin(item.roster, email)) plan.leaves.push(item);
            else if (hasOtherMembers(item.roster, email)) plan.blockers.push(item);
            else plan.deletes.push(item);
        }
    });
    return plan;
}

// ---------------------------------------------------------------------------
// Finding organizers
// ---------------------------------------------------------------------------

/**
 * Organizers whose name (or short name, or city) contains what was typed,
 * compared the way organizerNameKey compares names. Name matches first.
 */
export function searchOrganizers(organizers, typed, max = 5) {
    const key = organizerNameKey(typed);
    if (key.length < 2) return [];
    const scored = [];
    (organizers || []).forEach(o => {
        const nameKey = o.nameKey || organizerNameKey(o.name);
        let score = -1;
        if (nameKey.startsWith(key)) score = 0;
        else if (nameKey.includes(key)) score = 1;
        else if (organizerNameKey(o.shortName) === key) score = 1;
        else if (organizerNameKey(o.city).includes(key)) score = 2;
        if (score >= 0) scored.push({ o, score });
    });
    scored.sort((a, b) => a.score - b.score || String(a.o.name).localeCompare(String(b.o.name), 'nb'));
    return scored.slice(0, max).map(s => s.o);
}

// ---------------------------------------------------------------------------
// The Clubs & Organizers page
// ---------------------------------------------------------------------------

/**
 * Split "my organizers" into invitations (answer these) and memberships, each
 * sorted by name. Items carry `myRole` (see roleIn).
 */
export function groupMyOrganizers(items) {
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'nb', { sensitivity: 'base' });
    const invitations = (items || []).filter(o => isPendingRole(o.myRole)).sort(byName);
    const memberships = (items || []).filter(o => isMemberRole(o.myRole)).sort(byName);
    return { invitations, memberships };
}

// ---------------------------------------------------------------------------
// The tournament form
// ---------------------------------------------------------------------------

/**
 * The Organizer dropdown: the organizers this arbiter BELONGS to (an invitation
 * is not membership, and the rules would refuse it), sorted by name. A
 * tournament that already names a club the arbiter is not in — a co-arbiter's
 * view, or an arbiter since removed — keeps it as an option, so opening and
 * saving the settings does not quietly drop it.
 */
export function organizerOptions(myOrganizers, current = {}) {
    const options = (myOrganizers || [])
        .filter(o => isMemberRole(o.myRole))
        .map(o => ({ value: o.id, label: o.name || '', role: o.myRole }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nb', { sensitivity: 'base' }));
    const currentId = current && current.organizerId;
    if (currentId && !options.some(o => o.value === currentId)) {
        options.unshift({ value: currentId, label: current.organizerName || currentId, role: '' });
    }
    return options;
}

/**
 * What a new tournament should start with: the organizer used last time, if
 * this arbiter still belongs to it; otherwise none. Never guessed from the list
 * alone — an arbiter in two clubs has to pick.
 */
export function defaultOrganizerId(options, lastUsedId) {
    const usable = (options || []).filter(o => o.role);
    if (lastUsedId && usable.some(o => o.value === lastUsedId)) return lastUsedId;
    return '';
}

/** The two fields a tournament carries: the id and a copy of the name. */
export function tournamentOrganizerFields(organizerId, organizerName) {
    const id = String(organizerId || '');
    return { organizerId: id, organizerName: id ? String(organizerName || '') : '' };
}

// ---------------------------------------------------------------------------
// The public lists
// ---------------------------------------------------------------------------

/** Distinct organizers named by these tournaments, for the Organizer filter. */
export function publicOrganizerOptions(tournaments) {
    const seen = new Map();
    (tournaments || []).forEach(t => {
        const id = t && t.organizerId;
        if (id && !seen.has(id)) seen.set(id, String(t.organizerName || '').trim() || id);
    });
    return Array.from(seen, ([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nb', { sensitivity: 'base' }));
}

/** The Organizer filter: 'all' (or empty) matches every tournament. */
export function matchesOrganizer(tournament, organizerId) {
    if (!organizerId || organizerId === 'all') return true;
    return !!tournament && tournament.organizerId === organizerId;
}
